<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    :class="`${
      keyField === 'checklistHistoryNotes' ? 'tw-w-full' : ''
    } tw-flex tw-items-start tw-w-full tw-relative`"
  >
    <span
      v-if="!isEditStatus && keyField === 'checklistHistoryNotes'"
      class="tw-text-[13px] tw-text-[#231F20] tw-whitespace-pre-line"
      :class="customClass"
      v-html="item[keyField] || ''"
    />
    <span
      v-if="!isEditStatus && keyField !== 'checklistHistoryNotes'"
      class="tw-text-[13px] tw-text-[#231F20] tw-whitespace-pre-line"
      :class="customClass"
    >
      {{ item[keyField] }}
    </span>
    <span
      v-show="isEditStatus"
      class="tw-flex tw-rounded-[6px] tw-text-[13px] tw-text-[#231F20] tw-whitespace-nowrap tw-w-[100%] notes-input-field"
    >
      <textarea
        :id="`roadmap-do-what-input-${item.checklistGroupId}`"
        ref="autoTextarea"
        v-model="value"
        type="text"
        :class="[
          'input-field tw-border tw-border-solid tw-outline-none tw-w-full tw-p-[12px] tw-text-[13px] tw-rounded-md tw-font-normal',
          isErrorChecklistName ? '!tw-border-[#f7553c]' : 'tw-border-[#EFF0F1]',
          isCallApi ? '!tw-pr-[45px]' : '',
        ]"
        @keydown="(e: any) => handleKeyDown(e)"
        @focusout="() => onSaveData()"
        @input="autoResize"
      />
    </span>
    <b-form-invalid-feedback
      v-if="isErrorChecklistName"
      :class="{ 'is-invalid': isErrorChecklistName }"
    >
      <VRuntimeTemplate
        :template="getComponentUiText('app-common-form-value-required-error-1')"
      />
    </b-form-invalid-feedback>
    <div
      v-if="isCallApi"
      class="tw-h-[35px] tw-w-[35px] tw-absolute tw-right-[12px]"
    >
      <!-- eslint-disable vue/no-unused-refs -->
      <LottieAnimation
        ref="anim"
        :animation-data="ProgressAnimation"
        :auto-play="true"
        :loop="true"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import store from "@/store";
import { getComponentUiText } from "@/utils/utilities";
import VRuntimeTemplate from "vue3-runtime-template";
import { LottieAnimation } from "lottie-web-vue";
import ProgressAnimation from "@/assets/animation/progress_light.json";
import { successStatusCodes } from "@/utils/constants";
import { putChecklistStatus } from "@/services/assessment";
import { showErrorAPI, showToastMessage } from "@/services/apiRoutes";
import { ToastVariant } from "@/interfaces/common/IToastMessage";
import { Checklist } from "@/services/apiRoutes/routes";

const props = defineProps({
  isEditStatus: {
    type: Boolean,
    default: false,
  },
  item: {
    type: Object,
    required: true,
  },
  keyField: {
    type: String,
    required: true,
  },
  itemInvalids: {
    type: Array,
    default: () => [],
  },
  customClass: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["edit-notes", "update-notes"]);

const autoTextarea = ref<HTMLTextAreaElement | null>(null);
const value = ref(props.item[props.keyField] || "");

const roadMapErrorData = computed(() => {
  return store.state.Sharing.roadMapErrorData;
});

const isErrorChecklistName = computed(() => {
  return (
    (value.value === "" && props.keyField === "checklistGroupName") ||
    (props.itemInvalids || []).includes(props.item.checklistId)
  );
});

watch(
  () => isErrorChecklistName.value,
  (newValue) => {
    const errorData = roadMapErrorData.value || [];

    if (
      newValue &&
      !roadMapErrorData.value?.includes(props.item.checklistGroupId)
    ) {
      errorData.push(props.item.checklistGroupId);
    }
    if (
      !newValue &&
      roadMapErrorData.value?.includes(props.item.checklistGroupId)
    ) {
      const index = errorData.indexOf(props.item.checklistGroupId);
      if (index > -1) {
        errorData.splice(index, 1);
      }
    }

    store.commit("Sharing/SET_ROAD_MAP_ERROR_DATA", errorData);
  },
  { immediate: true },
);

watch(
  () => props.item[props.keyField],
  (newValue) => {
    if (newValue !== value.value) {
      value.value = newValue;
    }
  },
  { immediate: true },
);

watch(
  () => props.isEditStatus,
  (newValue) => {
    if (newValue) {
      autoResize();
    }
  },
);

onMounted(() => {
  if (props.isEditStatus) {
    autoResize();
  }
});

const onChangeStatusEdit = async () => {
  await onSaveData();
  emit("edit-notes");
};

const autoResize = (e?) => {
  if (
    e &&
    props.keyField === "checklistHistoryNotes" &&
    isErrorChecklistName.value
  ) {
    emit("edit-notes", e.target.value);
  }
  setTimeout(() => {
    if (autoTextarea.value) {
      autoTextarea.value.style.height = "44px"; // Reset height
      autoTextarea.value.style.height = autoTextarea.value.scrollHeight + "px"; // Set height based on content
    }
  }, 10);
};
const isCallApi = ref(false);
let dataRevert;

const onSaveData = async () => {
  try {
    value.value = value.value?.trim();
    if (props.keyField === "checklistHistoryNotes") {
      if (
        value.value === props.item[props.keyField] ||
        (!value.value && !props.item[props.keyField])
      )
        return;
      if (value.value === "") emit("edit-notes", value.value);
      else {
        isCallApi.value = true;
        const res = await putChecklistStatus(props.item.checklistId, {
          checklistStatusNotes: value.value,
          checklistStatusId: props.item.checklistStatusId,
        });
        if (res.status === successStatusCodes.OK) {
          emit("update-notes", value.value);
        } else {
          value.value = props.item[props.keyField];
        }
      }
      return;
    }
    if (
      value.value === props.item[props.keyField] ||
      isErrorChecklistName.value ||
      (!value.value && !props.item[props.keyField])
    ) {
      return;
    }

    const params = {
      checklistGroupId: props.item.checklistGroupId,
      ...(props.keyField === "checklistGroupActions"
        ? { actions: value.value }
        : {
            name: value.value,
          }),
    };
    isCallApi.value = true;
    dataRevert = props.item[props.keyField];
    const res = await store.dispatch("Sharing/updateRoadMapItemData", params);
    if (!res) {
      value.value = dataRevert;
    }
  } catch (error: any) {
    showErrorAPI(error, Checklist.updateChecklistShared);
  } finally {
    isCallApi.value = false;
  }
};

const handleKeyDown = (event: KeyboardEvent) => {
  if (props.keyField === "checklistHistoryNotes") {
    return;
  }
  if (event.key === "Enter" && !event.shiftKey) {
    event.preventDefault();
    onChangeStatusEdit();
  }
};
</script>

<style scoped>
.input-field {
  border-radius: 6px;
  border: 1px solid var(--UI-rule, #eff0f1);
  background: rgba(239, 240, 241, 0.5);
  color: #231f20;
  width: 100%;
  padding: 12px;
  line-height: 19.5px;
  resize: none;
  overflow-y: hidden;
}
</style>
